import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import i18next from "i18next";

import { FULL_TIME_SHIFTS_FILTERS } from "../../../assets/constants";
import axios from "../../../utils/api-client";
import { setSupervisorRateLimitError } from "./../../../features/user/user-slice";
import {
  FullTimeShift,
  FullTimeShiftsState,
  ShiftMeta, WorkerReassignment
} from "./full-time-shifts-type";

const initialState: FullTimeShiftsState = {
	fullTimeShifts: [],
	getFullTimeShifts: {
		status: "idle",
		errorMessage: "",
	},
	getFullTimeShiftsFilters: {
		status: "idle",
		errorMessage: "",
	},
	filterData: {
		allLocations: [],
		allWorkcenters: [],
		shiftPatterns: [],
		shiftPatternWorkCenter: [],
		jobs: [],
		schedulingStatuses: [],
		workerTypes: [],
		workerSubTypes: [],
    filterRelationships: [],
	},
	getFullTimeShiftsMeta: {
		status: "idle",
		errorMessage: "",
	},
	currentDate: new Date().toISOString(),
	selectedLocation: null, // Currently selected location ID
	shiftMetaList: [],
	getFullTimeShiftViewDetails: {
		status: "idle",
		errorMessage: "",
	},
	fullTimeShiftViewDetails: {
		shiftId: undefined,
		workcenterId: undefined,
		jobDemands: [],
		workers: [],
	},
	getRequestTypes: {
		status: "idle",
		errorMessage: "",
		requestTypes: [],
	},
	getRequestStatusTypes: {
		status: "idle",
		errorMessage: "",
		requestStatusTypes: [],
	},
	reassignWorkers: {
		status: "idle",
		errorMessage: "",
	},
  getLatestSchedulingStatus: {
    status: "idle",
    errorMessage: "",
  },
  executionDetails: {
    isPublished: true,
    executionStatus: "",
    lastExecutedById: null,
    lastExecutedByName: "",
    lastExecutedOn: "",
    isSaved: false,
  },
  isGenerateFullTimeShiftSchedulerModalOpen: false,
  generateSchedule: {
    status: "idle",
    errorMessage: "",
  },
  workerReassignments: [],
  saveOverrides: {
    status: "idle",
    errorMessage: "",
  },
  getEligibleWorkers: {
    status: "idle",
    errorMessage: "",
    eligibleWorkers: [],
  },
  getWorkersStaffingStatus: {
    status: "idle",
    errorMessage: "",
    workers: [],
    shiftId: "",
  }
};

export const getFullTimeShiftViewDetails = createAsyncThunk(
	"fullTimeShifts/getFullTimeShiftViewDetails",
	async (
		payload: { shiftId: string; workcenterId: number },
		{ rejectWithValue }
	) => {
		try {
			const response = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/FTShiftsView/GetFTShiftsExpandedDetails`,
				payload
			);
			return {
				...response.data,
				shiftId: payload.shiftId,
				workcenterId: payload.workcenterId,
			};
		} catch (error: any) {
			const errorResponse = error?.response?.data;
			errorResponse.status = error?.response?.status;
			try {
				if (errorResponse.status === 429) {
					return rejectWithValue(setSupervisorRateLimitError());
				} else if (errorResponse.status === 404) {
					return rejectWithValue({ error: i18next.t("full-time-shifts_no-data-found") });
				}
				return rejectWithValue({ error: errorResponse?.message ?? errorResponse ?? i18next.t("common_error-occurred") });
			} catch {
				return rejectWithValue(i18next.t("common_error-occurred"));
			}
		}
	}
);

export const getFullTimeShifts = createAsyncThunk(
	"fullTimeShifts/getFullTimeShifts",
	async (
		params: { startDate?: string; endDate?: string },
		{ dispatch, rejectWithValue }
	) => {
		try {
			const response = await axios.get<FullTimeShift[]>(
				`${process.env.REACT_APP_API_END_POINT}/FTShiftsView/GetFTShifts`,
				{
					params,
				}
			);
			return { data: response.data, currentDate: params.startDate };
		} catch (error: any) {
			const errorResponse = error?.response?.data;
			errorResponse.status = error?.response?.status;
			if (errorResponse.status === 429) {
				dispatch(setSupervisorRateLimitError());
				return rejectWithValue(errorResponse);
			}
			if (error.response.status === 404) {
				return rejectWithValue(error.response.data.message);
			}
			return rejectWithValue(error.response.data.message);
		}
	}
);

export const getFullTimeShiftsMeta = createAsyncThunk(
	"fullTimeShifts/getFullTimeShiftsMeta",
	async (payload, { rejectWithValue }) => {
		try {
			const response = await axios.post<ShiftMeta[]>(
				`${process.env.REACT_APP_API_END_POINT}/FTShiftsView/GetFTShiftsDetails`,
				payload
			);
			return response.data ?? [];
		} catch (error: any) {
			const errorResponse = error?.response.data;
			errorResponse.status = error?.response?.status;
			try {
				if (errorResponse.status === 429) {
					return rejectWithValue(setSupervisorRateLimitError());
				} else if (errorResponse.status === 404) {
					return rejectWithValue({ error: i18next.t("full-time-shifts_no-data-found") });
				}
				return rejectWithValue({ error: errorResponse?.message ?? errorResponse ?? i18next.t("common_error-occurred") });
			} catch {
				return rejectWithValue(i18next.t("common_error-occurred"));
			}
		}
	}
);

export const getFullTimeShiftsFilters = createAsyncThunk(
	"fullTimeShifts/getFullTimeShiftsFilters",
	async (_, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/FlexScheduler/GetFiltersForUser`,
				{ filters: FULL_TIME_SHIFTS_FILTERS }
			);

			if (res.status === 200) {
				return fulfillWithValue(res.data);
			} else if (res.status === 204) {
				return fulfillWithValue({
					locations: [],
					workCenters: [],
					shiftPatterns: [],
					shiftPatternWorkCenter: [],
					jobs: [],
				});
			}
			return rejectWithValue(i18next.t("common_error-occurred"));
		} catch (error: any) {
			const errorResponse = error?.response.data;
			errorResponse.status = error?.response?.status;
			try {
				if (errorResponse.status === 429) {
					return rejectWithValue(setSupervisorRateLimitError());
				} else if (errorResponse.status === 404) {
					return rejectWithValue({ error: i18next.t("full-time-shifts_no-data-found") });
				}
				return rejectWithValue({ error: errorResponse?.message ?? errorResponse ?? i18next.t("common_error-occurred") });
			} catch {
				return rejectWithValue(i18next.t("common_error-occurred"));
			}
		}
	}
);

export const fetchRequestTypes = createAsyncThunk(
	"fullTimeShifts/fetchRequestTypes",
	async (_, { rejectWithValue }) => {
		try {
			const response = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/workers/requestTypes`
			);
			return response.data;
		} catch (error: any) {
			const errorResponse = error?.response?.data;
			errorResponse.status = error?.response?.status;
			try {
				if (errorResponse.status === 429) {
					return rejectWithValue(setSupervisorRateLimitError());
				} else if (errorResponse.status === 404) {
					return rejectWithValue({ error: i18next.t("full-time-shifts_no-data-found") });
				}
				return rejectWithValue({ error: errorResponse?.message ?? errorResponse ?? i18next.t("common_error-occurred") });
			} catch {
				return rejectWithValue(i18next.t("common_error-occurred"));
			}
		}
	}
);

export const getRequestStatusTypes = createAsyncThunk(
	"fullTimeShifts/getAllRequestsTypes",
	async (payload, { fulfillWithValue, rejectWithValue, dispatch }) => {
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/workers/requestStatuses`
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			const errorResponse = error?.response?.data;
			errorResponse.status = error?.response?.status;
			try {
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				return rejectWithValue(error.response.data.message);
			} catch (e) {
				return rejectWithValue(i18next.t("common_error-occurred"));
			}
		}
	}
);

export const reassignWorkers = createAsyncThunk(
	"fullTimeShifts/reassignWorkers",
	async (
		payload: {
			ftShiftId: string;
			workcenterId: number;
			locationId: number;
			jobId: number;
			workerId: string;
		}[],
		{ rejectWithValue }
	) => {
		try {
			const response = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/FTSchedulingStatus/ReassignWorkers`,
				payload
			);
			return response.data;
		} catch (error: any) {
			const errorResponse = error?.response?.data;
			errorResponse.status = error?.response?.status;
			try {
				if (errorResponse.status === 429) {
					return rejectWithValue(setSupervisorRateLimitError());
				} else if (errorResponse.status === 404) {
					return rejectWithValue({ error: i18next.t("full-time-shifts_no-data-found") });
				}
				return rejectWithValue({ error: errorResponse?.message ?? errorResponse ?? i18next.t("common_error-occurred") });
			} catch {
				return rejectWithValue(i18next.t("common_error-occurred"));
			}
		}
	}
);

export const getLatestSchedulingStatus = createAsyncThunk(
  "fullTimeShifts/getLatestSchedulingStatus",
  async (payload: { locationId: number; fromDate: string, toDate: string}, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_END_POINT}/ftscheduler/execution-details`,
        payload
      );
      if (response.status === 200) {
        return response.data;
      } else if (response.status === 204) {
        return {
          isPublished: false,
          executionStatus: "",
          lastExecutedById: null,
          lastExecutedByName: "",
          lastExecutedOn: "",
          isSaved: false,
        };
      }
    } catch (error: any) {
      const errorResponse = error?.response?.data;
      errorResponse.status = error?.response?.status;
      try {
        if (errorResponse.status === 429) {
          return rejectWithValue(setSupervisorRateLimitError());
        } else if (errorResponse.status === 404) {
          return rejectWithValue({ error: i18next.t("full-time-shifts_no-data-found") });
        }
        return rejectWithValue({ error: errorResponse?.message ?? errorResponse ?? i18next.t("common_error-occurred") });
      } catch {
        return rejectWithValue(i18next.t("common_error-occurred"));
      }
    }
  }
);

export const getEligibleWorkers = createAsyncThunk(
  "fullTimeShifts/getEligibleWorkers",
  async (payload: { jobIds: number[]; shiftId: string }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_END_POINT}/FTShiftsView/GetEligibleWorkers`,
        payload
      );
      return response.data;
    } catch (error: any) {
      const errorResponse = error?.response?.data;
      errorResponse.status = error?.response?.status;
      try {
        if (errorResponse.status === 429) {
          return rejectWithValue(setSupervisorRateLimitError());
        } else if (errorResponse.status === 404) {
          return rejectWithValue({ error: i18next.t("full-time-shifts_no-data-found") });
        }
        return rejectWithValue({ error: errorResponse?.message ?? errorResponse ?? i18next.t("common_error-occurred") });
      } catch {
        return rejectWithValue(i18next.t("common_error-occurred"));
      }
    }
  }
);

export const getWorkersStaffingStatus = createAsyncThunk(
  "fullTimeShifts/getWorkersStaffingStatus",
  async (payload: { workerIds: string[]; shiftId: string }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_END_POINT}/FTShiftsView/getWorkersStaffingStatus`,
        payload
      );
      return {...response.data,
      shiftId: payload.shiftId};
    } catch (error: any) {
      const errorResponse = error?.response?.data;
      errorResponse.status = error?.response?.status;
      try {
        if (errorResponse.status === 429) {
          return rejectWithValue(setSupervisorRateLimitError());
        } else if (errorResponse.status === 404) {
          return rejectWithValue({ error: i18next.t("full-time-shifts_no-data-found") });
        }
        return rejectWithValue({ error: errorResponse?.message ?? errorResponse ?? i18next.t("common_error-occurred") });
      } catch {
        return rejectWithValue(i18next.t("common_error-occurred"));
      }
    }
  }
);

export const generateSchedule = createAsyncThunk(
  "fullTimeShifts/generateSchedule",
  async (
    payload: { locationId: number; fromDate: string; toDate: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_END_POINT}/ftscheduler/schedule`,
        payload
      );
      const reassignments = response.data?.workerReassignments as WorkerReassignment[];
      const patternIds = [
        ...new Set(
          reassignments
            .filter((r) => r.isPatternChange)
            .map((r) => r.to.patternId)
        ),
      ];
      if (patternIds.length === 0) {
        return { workerReassignments: reassignments };
      }
      const shiftInstanceResponse = await axios.post(
        `${process.env.REACT_APP_API_END_POINT}/FTWorkerView/GetShiftInstancesForPattern`,
        {
          startDate: payload.fromDate,
          endDate: payload.toDate,
          patternIds,
        }
      );
      const shiftInstances = shiftInstanceResponse.data as {
        patternId: number;
        ftShiftInstanceId: string;
        startDateTime: string;
        endDateTime: string;
      }[];
      const shiftsByPattern: Record<number,
        {
          ftShiftInstanceId: string;
          startDateTime: string;
          endDateTime: string;
        }[]> = {};

      for (const shift of shiftInstances) {
        if (!shiftsByPattern[shift.patternId]) {
          shiftsByPattern[shift.patternId] = [];
        }
        shiftsByPattern[shift.patternId].push(shift);
      }
      const updatedReassignments: WorkerReassignment[] = [];

      for (const r of reassignments) {
        if (r.isPatternChange && r.shiftId === null) {
          const matchingShifts = shiftsByPattern[r.to.patternId];
          if (matchingShifts?.length) {
            for (const shift of matchingShifts) {
              updatedReassignments.push({
                ...r,
                shiftId: shift.ftShiftInstanceId,
                startDate: shift.startDateTime,
                endDate: shift.endDateTime,
              });
            }
          } else {
            updatedReassignments.push(r); // fallback: keep original
          }
        } else {
          updatedReassignments.push(r); // unchanged
        }
      }

      return { workerReassignments: updatedReassignments };
    } catch (error: any) {
      const errorResponse = error?.response?.data;
      errorResponse.status = error?.response?.status;
      try {
        if (errorResponse.status === 429) {
          return rejectWithValue(setSupervisorRateLimitError());
        } else if (errorResponse.status === 404) {
          return rejectWithValue({ error: i18next.t("full-time-shifts_no-data-found") });
        }
        return rejectWithValue({ error: errorResponse?.message ?? errorResponse ?? i18next.t("common_error-occurred") });
      } catch {
        return rejectWithValue(i18next.t("common_error-occurred"));
      }
    }
  }
);

export const saveOverrides = createAsyncThunk(
  "fullTimeShifts/saveOverrides",
  async (
    payload: {
      reassignmentIds: number[];
      locationId: number;
      fromDate: string;
      toDate: string;
    },
    { rejectWithValue }
  ) => {
    try {
	const overridesApiBody = {
    locationId: payload?.locationId,
    scheduledOverrideIds: payload?.reassignmentIds};

      const response = await axios.post(
        `${process.env.REACT_APP_API_END_POINT}/ftscheduler/save-overrides`,
        overridesApiBody
      );
      const executionDetailsResponse = await axios.post(
        `${process.env.REACT_APP_API_END_POINT}/ftscheduler/execution-details`,
        {
          locationId: payload.locationId,
          fromDate: payload.fromDate,
          toDate: payload.toDate,
        }
      );

      return {
        executionDetails: executionDetailsResponse.data,
        saveOverrides: response.data,
      };
    } catch (error: any) {
      const errorResponse = error?.response?.data;
      errorResponse.status = error?.response?.status;
      try {
        if (errorResponse.status === 429) {
          return rejectWithValue(setSupervisorRateLimitError());
        } else if (errorResponse.status === 404) {
          return rejectWithValue({ error: i18next.t("full-time-shifts_no-data-found") });
        }
        return rejectWithValue({ error: errorResponse?.message ?? errorResponse ?? i18next.t("common_error-occurred") });
      } catch {
        return rejectWithValue(i18next.t("common_error-occurred"));
      }
    }
  });

const workerSchedulerSlice = createSlice({
	name: "fullTimeShifts",
	initialState,
	reducers: {
		resetFullTimeShifts: (state) => {
			state.fullTimeShifts = [];
			state.getFullTimeShifts.status = "idle";
			state.getFullTimeShifts.errorMessage = "";
		},
		setSelectedLocation: (state, action) => {
			state.selectedLocation = action.payload;
		},
		resetShiftsMeta: (state) => {
			state.shiftMetaList = [];
			state.getFullTimeShiftsMeta.status = "idle";
			state.getFullTimeShiftsMeta.errorMessage = "";
		},
		resetReassignWorkers: (state) => {
			state.reassignWorkers.status = "idle";
			state.reassignWorkers.errorMessage = "";
		},
		resetFullTimeShiftViewDetails: (state) => {
		state.getFullTimeShiftViewDetails.status = "idle";
		state.getFullTimeShiftViewDetails.errorMessage = "";
		},
		setCurrentDate: (state, action) => {
			state.currentDate = action.payload;
		},
    resetGetLatestSchedulingStatus: (state) => {
      state.getLatestSchedulingStatus.status = "idle";
      state.getLatestSchedulingStatus.errorMessage = "";
      state.executionDetails = {
        isPublished: true,
        executionStatus: "",
        lastExecutedById: null,
        lastExecutedByName: "",
        lastExecutedOn: "",
        isSaved: false,
      };
    },
    openGenerateFullTimeShiftSchedulerModal: (state) => {
      state.isGenerateFullTimeShiftSchedulerModalOpen = true;
    },
    closeGenerateFullTimeShiftSchedulerModal: (state) => {
      state.isGenerateFullTimeShiftSchedulerModalOpen = false;
    },
    resetGenerateSchedule: (state) => {
      state.generateSchedule.status = "idle";
      state.generateSchedule.errorMessage = "";
    },
    selectWorkerReassignment: (state, action) => {
      const reassignmentId = action.payload;
      const reassignments = state.workerReassignments.filter(
        (r) => r.id === reassignmentId
      );
      if (reassignments) {
        reassignments.forEach((reassignment) => {
          reassignment.isSelected = !reassignment.isSelected;
        });
      }
    },
    resetSaveOverrides: (state) => {
      state.saveOverrides.status = "idle";
      state.saveOverrides.errorMessage = "";
    },
	},
	extraReducers: (builder) => {
		builder.addCase(getFullTimeShifts.pending, (state) => {
			state.getFullTimeShifts.status = "pending";
			state.getFullTimeShifts.errorMessage = "";
		});
		builder.addCase(getFullTimeShifts.fulfilled, (state, action) => {
			state.getFullTimeShifts.status = "fulfilled";
			state.currentDate =
				action.payload?.currentDate ?? new Date().toISOString();
			state.fullTimeShifts = action.payload?.data;
		});
		builder.addCase(getFullTimeShifts.rejected, (state, action: any) => {
			state.getFullTimeShifts.status = "error";
			state.getFullTimeShifts.errorMessage = action.payload ? typeof action.payload?.error === "string"
  ? action.payload.error
  : i18next.t("common_error-occurred") : i18next.t("common_error-occurred");
		});
		builder.addCase(getFullTimeShiftsFilters.pending, (state) => {
			state.getFullTimeShiftsFilters.status = "pending";
			state.getFullTimeShiftsFilters.errorMessage = "";
		});
		builder.addCase(getFullTimeShiftsFilters.fulfilled, (state, action) => {
			state.getFullTimeShiftsFilters.status = "fulfilled";
			state.filterData.allLocations = action.payload?.locations ?? [];
			state.filterData.allWorkcenters = action.payload?.workCenters ?? [];
			state.filterData.shiftPatterns = action.payload?.shiftPatterns ?? [];
			state.filterData.shiftPatternWorkCenter =
				action.payload?.shiftPatternWorkCenters ?? [];
			state.filterData.jobs = action.payload?.jobs ?? [];
			state.filterData.schedulingStatuses =
        action.payload?.schedulingStatuses?.map(
          (status: { code: string; name: any }) => {
            return {
              ...status,
              name: status.code === "COF" ? i18next.t("full-time-shifts_time-off") : status.name,
            };
          }
        ) ?? [];
			state.filterData.workerTypes = action.payload?.workerTypes ?? [];
			state.filterData.workerSubTypes = action.payload?.workerSubTypes ?? [];
      state.filterData.filterRelationships = action.payload?.filterRelationships ?? [];
		});
		builder.addCase(getFullTimeShiftsFilters.rejected, (state, action: any) => {
			state.getFullTimeShiftsFilters.status = "error";
			state.getFullTimeShiftsFilters.errorMessage = action.payload ? typeof action.payload?.error === "string"
  ? action.payload.error
  : i18next.t("common_error-occurred") : i18next.t("common_error-occurred");
		});
		builder.addCase(getFullTimeShiftsMeta.pending, (state) => {
			state.getFullTimeShiftsMeta.status = "pending";
			state.getFullTimeShiftsMeta.errorMessage = "";
		});
		builder.addCase(getFullTimeShiftsMeta.fulfilled, (state, action) => {
			const incoming = action.payload;
			const existingMap = new Map(
				state.shiftMetaList.map((meta) => [
					`${meta.shiftId}_${meta.workcenterId}`,
					meta,
				])
			);

			incoming.forEach((meta) => {
				const key = `${meta.shiftId}_${meta.workcenterId}`;
				existingMap.set(key, meta); // Add or update
			});

			state.shiftMetaList = Array.from(existingMap.values());
			state.getFullTimeShiftsMeta.status = "fulfilled";
			state.getFullTimeShiftsMeta.errorMessage = "";
		});
		builder.addCase(getFullTimeShiftsMeta.rejected, (state, action: any) => {
			state.shiftMetaList = [];
			state.getFullTimeShiftsMeta.status = "error";
			state.getFullTimeShiftsMeta.errorMessage = action.payload ? typeof action.payload?.error === "string"
  ? action.payload.error
  : i18next.t("common_error-occurred") : i18next.t("common_error-occurred");
		});
		builder.addCase(getFullTimeShiftViewDetails.pending, (state) => {
			state.getFullTimeShiftViewDetails.status = "pending";
			state.getFullTimeShiftViewDetails.errorMessage = "";
			state.fullTimeShiftViewDetails = {
				shiftId: undefined,
				workcenterId: undefined,
				jobDemands: [],
				workers: [],
			};
		});
		builder.addCase(getFullTimeShiftViewDetails.fulfilled, (state, action) => {
			state.getFullTimeShiftViewDetails.status = "fulfilled";
			state.fullTimeShiftViewDetails = action.payload;
		});
		builder.addCase(getFullTimeShiftViewDetails.rejected, (state, action: any) => {
			state.getFullTimeShiftViewDetails.status = "error";
			state.getFullTimeShiftViewDetails.errorMessage = action.payload ? typeof action.payload?.error === "string"
  ? action.payload.error
  : i18next.t("common_error-occurred") : i18next.t("common_error-occurred");
			state.fullTimeShiftViewDetails = {
				shiftId: undefined,
				workcenterId: undefined,
				jobDemands: [],
				workers: [],
			};
		});
		builder.addCase(fetchRequestTypes.pending, (state) => {
			state.getRequestTypes.status = "pending";
			state.getRequestTypes.errorMessage = "";
		});
		builder.addCase(fetchRequestTypes.rejected, (state: any, action: any) => {
			state.getRequestTypes.status = "error";
			state.getRequestTypes.errorMessage = action.payload?.message;
			state.getRequestTypes.requestTypes = [];
		});
		builder.addCase(fetchRequestTypes.fulfilled, (state, action) => {
			state.getRequestTypes.status = "fulfilled";
			state.getRequestTypes.errorMessage = "";
			state.getRequestTypes.requestTypes = action.payload ?? [];
		});
		builder.addCase(getRequestStatusTypes.pending, (state) => {
			state.getRequestStatusTypes.status = "pending";
			state.getRequestStatusTypes.errorMessage = "";
		});
		builder.addCase(
			getRequestStatusTypes.rejected,
			(state: any, action: any) => {
				state.getRequestStatusTypes.status = "error";
				state.getRequestStatusTypes.errorMessage = action.payload?.message;
				state.getRequestStatusTypes.requestStatusTypes = [];
			}
		);
		builder.addCase(getRequestStatusTypes.fulfilled, (state, action) => {
			state.getRequestStatusTypes.status = "fulfilled";
			state.getRequestStatusTypes.errorMessage = "";
			state.getRequestStatusTypes.requestStatusTypes =
				action.payload?.fullTime ?? [];
		});
		builder.addCase(reassignWorkers.pending, (state, action) => {
			state.reassignWorkers.status = "pending";
			state.reassignWorkers.errorMessage = "";
		});
		builder.addCase(reassignWorkers.fulfilled, (state, action) => {
			state.reassignWorkers.status = "fulfilled";
			state.reassignWorkers.errorMessage = "";
		});
		builder.addCase(reassignWorkers.rejected, (state, action: any) => {
			state.reassignWorkers.status = "error";
			state.reassignWorkers.errorMessage = action.payload ? typeof action.payload?.error === "string"
  ? action.payload.error
  : i18next.t("common_error-occurred") : i18next.t("common_error-occurred");
		});
    builder.addCase(getLatestSchedulingStatus.pending, (state) => {
      state.getLatestSchedulingStatus.status = "pending";
      state.getLatestSchedulingStatus.errorMessage = "";
    });
    builder.addCase(getLatestSchedulingStatus.fulfilled, (state, action) => {
      state.getLatestSchedulingStatus.status = "fulfilled";
      state.executionDetails = {
        isPublished: action.payload?.isPublished ?? false,
        executionStatus: action.payload?.executionStatus ?? "",
        lastExecutedById: action.payload?.lastExecutedById ?? null,
        lastExecutedByName: action.payload?.lastExecutedByName ?? "",
        lastExecutedOn: action.payload?.lastExecutedOn ?? "",
        isSaved: action.payload?.isSaved ?? false,
      }
    });
    builder.addCase(getLatestSchedulingStatus.rejected, (state, action: any) => {
      state.getLatestSchedulingStatus.status = "error";
      state.getLatestSchedulingStatus.errorMessage = action.payload ? typeof action.payload?.error === "string"
  ? action.payload.error
  : i18next.t("common_error-occurred") : i18next.t("common_error-occurred");
    });
    builder.addCase(generateSchedule.pending, (state) => {
      state.generateSchedule.status = "pending";
      state.generateSchedule.errorMessage = "";
    });
    builder.addCase(generateSchedule.fulfilled, (state, action) => {
      state.generateSchedule.status = "fulfilled";
      state.generateSchedule.errorMessage = "";
      state.workerReassignments = action.payload?.workerReassignments?.map(
        (reassignment: WorkerReassignment) => ({
          ...reassignment,
          isSelected: true, // Default to selected
        })
      ) ?? [];
    });
    builder.addCase(generateSchedule.rejected, (state, action: any) => {
      state.generateSchedule.status = "error";
      state.generateSchedule.errorMessage = action.payload ? typeof action.payload?.error === "string"
  ? action.payload.error
  : i18next.t("common_error-occurred") : i18next.t("common_error-occurred");
    });
    builder.addCase(saveOverrides.pending, (state) => {
      state.saveOverrides.status = "pending";
      state.saveOverrides.errorMessage = "";
    });
    builder.addCase(saveOverrides.fulfilled, (state, action) => {
      const executionDetails = action.payload?.executionDetails;
      state.saveOverrides.status = "fulfilled";
      state.saveOverrides.errorMessage = "";
      state.executionDetails.isSaved = true;
      state.getLatestSchedulingStatus.status = "fulfilled";
      state.executionDetails = {
        isPublished: executionDetails?.isPublished ?? false,
        executionStatus: executionDetails?.executionStatus ?? "",
        lastExecutedById: executionDetails?.lastExecutedById ?? null,
        lastExecutedByName: executionDetails?.lastExecutedByName ?? "",
        lastExecutedOn: executionDetails?.lastExecutedOn ?? "",
        isSaved: executionDetails?.isSaved ?? false,
      };
    });
    builder.addCase(saveOverrides.rejected, (state, action: any) => {
      state.saveOverrides.status = "error";
      state.saveOverrides.errorMessage = action.payload ? typeof action.payload?.error === "string"
  ? action.payload.error
  : i18next.t("common_error-occurred") : i18next.t("common_error-occurred");
    });
    builder.addCase(getEligibleWorkers.rejected, (state, action: any) => {
      state.getEligibleWorkers.status = "error";
      state.getEligibleWorkers.errorMessage = action.payload ? typeof action.payload?.error === "string"
        ? action.payload.error
        : i18next.t("common_error-occurred") : i18next.t("common_error-occurred");
    });
    builder.addCase(getEligibleWorkers.pending, (state) => {
      state.getEligibleWorkers.status = "pending";
      state.getEligibleWorkers.errorMessage = "";
    });
    builder.addCase(getEligibleWorkers.fulfilled, (state, action: any) => {
      state.getEligibleWorkers.status = "fulfilled";
      state.getEligibleWorkers.errorMessage = "";
      let workers = action.payload?.workers ?? [];
      workers = workers.map((worker: any) => {
        const workerTypeName = worker.workerTypeId ?
          state.filterData.workerTypes?.find((type) => type.id === worker.workerTypeId)?.name
          : "";
        const workerSubTypeName = worker.workerSubTypeId ?
          state.filterData.workerSubTypes?.find((subType) => subType.id === worker.workerSubTypeId)?.name
          : "";
        const workerTypeAndSubTypeString = `${workerTypeName}${workerSubTypeName !== "" ? ` - ${workerSubTypeName}` : ""}`.trim();
        const name = `${worker.firstName} ${worker.lastName}`.trim() || i18next.t("full-time-shifts_unnamed");
        const initials = `${worker.firstName ? worker.firstName[0] : ""}${worker.lastName ? worker.lastName[0] : ""}`.toUpperCase();
        return {
        ...worker,
        id: worker.workerId,
        initials: initials,
        name: name,
        info: `${worker.badgeNumber ? worker.badgeNumber
          : worker.companyEmployeeId} (${workerTypeAndSubTypeString})`,
      }});
      state.getEligibleWorkers.eligibleWorkers = workers ?? [];
    });
    builder.addCase(getWorkersStaffingStatus.pending, (state) => {
      state.getWorkersStaffingStatus.status = "pending";
      state.getWorkersStaffingStatus.errorMessage = "";
    });
    builder.addCase(getWorkersStaffingStatus.rejected, (state, action: any) => {
      state.getWorkersStaffingStatus.status = "error";
      state.getWorkersStaffingStatus.errorMessage = action.payload ? typeof action.payload?.error === "string"
        ? action.payload.error
        : i18next.t("common_error-occurred") : i18next.t("common_error-occurred");
    });
    builder.addCase(getWorkersStaffingStatus.fulfilled, (state, action: any) => {
      state.getWorkersStaffingStatus.status = "fulfilled";
      state.getWorkersStaffingStatus.errorMessage = "";
      const newWorkers = action.payload?.workers ?? [];
      const newShiftId = action.payload?.shiftId ?? "";
      const existingWorkers = state.getWorkersStaffingStatus.workers ?? [];
      const existingShiftId = state.getWorkersStaffingStatus.shiftId ?? "";

      if (newShiftId !== existingShiftId) {
        // Different shift - replace entire workers array
        state.getWorkersStaffingStatus.workers = newWorkers;
        state.getWorkersStaffingStatus.shiftId = newShiftId;
      } else {
        // Same shift - merge workers
        const existingWorkerIds = new Set(
          existingWorkers.map((w) => w.workerId)
        );
        const workersToAdd = newWorkers.filter(
          (w: { workerId: string }) => !existingWorkerIds.has(w.workerId)
        );
        state.getWorkersStaffingStatus.workers = [
          ...existingWorkers,
          ...workersToAdd,
        ];
      }
    });
	},
});

export const {
	resetFullTimeShifts,
	resetShiftsMeta,
	setSelectedLocation,
	resetReassignWorkers,
	resetFullTimeShiftViewDetails,
	setCurrentDate,
  resetGetLatestSchedulingStatus,
  openGenerateFullTimeShiftSchedulerModal,
  closeGenerateFullTimeShiftSchedulerModal,
  resetGenerateSchedule,
  selectWorkerReassignment,
  resetSaveOverrides
} = workerSchedulerSlice.actions;

export default workerSchedulerSlice.reducer;
